import { css } from 'aphrodite';
import * as React from 'react';
import type { ISupportTicket } from '../../../models/Support/ISupportTicket';
import { ThreadType } from '../../../models/Support/SupportEnums';
import { useSupportCategoriesQuery } from '../../../queries/Support/useSupportCategories';
import { useSupportTeamQuery } from '../../../queries/Support/useSupportTeam';
import { useSupportThreadsQuery } from '../../../queries/Support/useSupportThreadsQuery';
import { useSupportTicketQuery } from '../../../queries/Support/useSupportTicketQuery';
import { useSupportTicketsQuery } from '../../../queries/Support/useSupportTicketsQuery';
import {
	getInitials,
	getSupportContactProfilePicSrc,
	getUserReferenceName,
} from '../../../utils/Support/supportHelpers';
import { Avatar } from '@HoudiniUI/Avatar';
import { AssigneeIcon } from '../../../web/components/svgs/icons/AssigneeIcon';
import { MessageCircleIcon } from '../../../web/components/svgs/icons/MessageCircleIcon';
import { SparklesIcon } from '../../../web/components/svgs/icons/SparklesIcon';
import { Button } from '../../../web/components-v2/Button';
import {
	AdminDrawerToolbar,
	AppDrawerContent,
	AppDrawerProvider,
	AppDrawerSeparator,
	AppDrawerToolbarButton,
	SideDrawer,
} from '../../../web/components-v2/Drawer/AppDrawer';
import { AdminAppAssistantChat } from '../../../web/containers/templates/AssistantChat';
import { CreateGitHubIssueModal, LinkGitHubIssueModal } from './GitHubIssueModals';
import { GitHubPanel } from './GitHubPanel';
import { inboxStyles as s } from './styles/inboxStyles';
import { formatDate, getTicketContactLabel } from './supportTicketPresentation';
import { TicketDetailsEditor } from './TicketDetailsEditor';
import { TicketPreviewModal } from './TicketPreviewModal';

type RightTab = 'assistant' | 'details';

interface RightPanelProps {
	ticketId: string;
	/** Default drawer panel for a fresh selection. The support desk default is Details. */
	defaultTab?: RightTab;
	/** Navigate the inbox to another ticket from the previous-conversations preview modal. */
	onSelectTicket?: (ticketId: string) => void;
	/** Opens the customer picker for customerless tickets. */
	onChangeCustomer?: () => void;
	/** Called after the ticket leaves the agent's plate (closed / assigned) — parent advances to the next ticket. */
	onTicketLeftView?: () => void;
}

const SUPPORT_INBOX_DRAWER_PERSIST_KEY = 'support-inbox-active-drawer-key';
const DETAILS_DRAWER_VALUE = 'support-ticket-details';
const ASSISTANT_DRAWER_VALUE = 'support-ticket-assistant';

const getDrawerValue = (tab: RightTab) => (tab === 'assistant' ? ASSISTANT_DRAWER_VALUE : DETAILS_DRAWER_VALUE);

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
	<div className={css(s.detailRow)}>
		<span className={css(s.detailKey)}>{label}</span>
		<span className={css(s.detailValue)}>{children}</span>
	</div>
);

const PREVIOUS_CONVERSATION_LIMIT = 6;

type PreviousConversationTicket = Pick<ISupportTicket, 'id' | 'number' | 'subject'>;

const PreviousConversations: React.FC<{
	tickets: PreviousConversationTicket[];
	onPreviewTicket: (ticketId: string) => void;
}> = ({ tickets, onPreviewTicket }) => {
	const [showAll, setShowAll] = React.useState(false);
	if (tickets.length === 0) return null;

	const visibleTickets = showAll ? tickets : tickets.slice(0, PREVIOUS_CONVERSATION_LIMIT);
	const hasOverflow = tickets.length > PREVIOUS_CONVERSATION_LIMIT;

	return (
		<>
			<div className={css(s.detailSectionTitle)}>Conversations ({tickets.length})</div>
			<div className={css(s.linkedList)}>
				{visibleTickets.map(ticket => (
					<Button
						key={ticket.id}
						variant='ghost'
						size='sm'
						styles={[s.linkedRow]}
						onClick={() => onPreviewTicket(ticket.id)}
						title={`#${ticket.number} · ${ticket.subject}`}
					>
						<span className={css(s.linkedIcon)}>
							<MessageCircleIcon width={14} height={14} />
						</span>
						<span className={css(s.linkedText)}>{ticket.subject}</span>
					</Button>
				))}
				{hasOverflow && (
					<Button variant='ghost' size='sm' styles={[s.linkedRow]} onClick={() => setShowAll(value => !value)}>
						<span className={css(s.linkedIcon)}>&#8943;</span>
						<span className={css(s.linkedText, s.linkedViewAll)}>
							{showAll ? 'Show less' : `View all ${tickets.length}`}
						</span>
					</Button>
				)}
			</div>
		</>
	);
};

/**
 * Support inbox drawer. This intentionally uses the product AppDrawer primitives, but mounts them
 * inside the support desk page grid instead of `AdminDashboardPageLayout.sidebarChildren`; that keeps
 * the admin masthead/user-menu anchored while the drawer opens and closes.
 */
export const RightPanel: React.FC<RightPanelProps> = ({
	ticketId,
	defaultTab = 'details',
	onSelectTicket,
	onChangeCustomer,
	onTicketLeftView,
}) => {
	const [drawerValue, setDrawerValue] = React.useState(() => getDrawerValue(defaultTab));
	const [gitHubModal, setGitHubModal] = React.useState<'create' | 'link' | null>(null);
	const [previewTicketId, setPreviewTicketId] = React.useState<string | null>(null);

	// A new ticket selection should start on the details panel, matching the updated support desk UX.
	React.useEffect(() => setDrawerValue(getDrawerValue(defaultTab)), [ticketId, defaultTab]);
	// Close transient modals when switching tickets, or when leaving the Details panel.
	React.useEffect(() => {
		setGitHubModal(null);
		setPreviewTicketId(null);
	}, [ticketId]);
	React.useEffect(() => {
		if (drawerValue !== DETAILS_DRAWER_VALUE) {
			setGitHubModal(null);
			setPreviewTicketId(null);
		}
	}, [drawerValue]);

	const { data: ticket } = useSupportTicketQuery(ticketId, { refetchInterval: false });
	const { data: threads } = useSupportThreadsQuery(ticketId, { refetchInterval: false });
	const categoriesQuery = useSupportCategoriesQuery(ticket?.mailboxId || '');
	const categories = categoriesQuery.data?.values ?? [];
	const { data: teamData } = useSupportTeamQuery(ticket?.mailboxId || '');
	const teamMembers = React.useMemo(
		() => (teamData?.values ?? []).map(a => ({ id: a.agentId, name: a.agentName || a.agentId })),
		[teamData]
	);

	const linkedReady = !!ticket?.mailboxId && !!ticket?.contactEmail;
	const linkedQuery = useSupportTicketsQuery({
		mailboxId: linkedReady ? ticket.mailboxId : '',
		contactEmail: ticket?.contactEmail,
		includeInactive: true,
		page: 1,
		pageSize: 100,
		refetchInterval: false,
	});
	const linkedTicketValues = linkedQuery.data?.values;
	const linkedTickets = React.useMemo(
		() =>
			(linkedTicketValues ?? [])
				.filter(linkedTicket => linkedTicket.id !== ticketId)
				.sort((a, b) => new Date(b.creationDate).getTime() - new Date(a.creationDate).getTime()),
		[linkedTicketValues, ticketId]
	);

	const messageCount = threads?.values?.filter(t => t.type !== ThreadType.Note && t.type !== ThreadType.AiDraft).length;
	const noteCount = threads?.values?.filter(t => t.type === ThreadType.Note).length;
	const contactLabel = ticket ? getTicketContactLabel(ticket) : 'Contact';
	const hasCustomer = !!ticket?.contactEmail?.trim();

	return (
		<AppDrawerProvider value={drawerValue} onValueChange={setDrawerValue} persistKey={SUPPORT_INBOX_DRAWER_PERSIST_KEY}>
			<SideDrawer styles={[s.supportDrawer]}>
				<AppDrawerContent value={DETAILS_DRAWER_VALUE} styles={[s.supportDrawerContent]}>
					{!ticket ? (
						<div className={css(s.empty)}>Select a ticket to see details.</div>
					) : (
						<div className={css(s.rightContent)}>
							<div className={css(s.detailHeader)}>
								<Avatar
									imgSrc={getSupportContactProfilePicSrc(ticket)}
									altText={contactLabel === '—' ? 'Contact' : contactLabel}
									fallbackText={getInitials(ticket.contactName, ticket.contactEmail)}
									styles={[s.detailAvatar]}
								/>
								<div>
									<div className={css(s.detailName)}>
										{ticket.contactName || (hasCustomer ? '—' : 'No customer assigned')}
									</div>
									{hasCustomer ? (
										<div className={css(s.detailEmail)}>{ticket.contactEmail}</div>
									) : (
										onChangeCustomer && (
											<Button type='button' variant='link' styles={[s.detailCustomerAction]} onClick={onChangeCustomer}>
												Add customer
											</Button>
										)
									)}
								</div>
							</div>

							<div className={css(s.detailSectionTitle)}>Account</div>
							<div className={css(s.detailRows)}>
								{ticket.accountName && (
									<Row label='Company'>
										<a href={`/admin.html#/accounts/${ticket.accountId}`} className={css(s.detailLink)}>
											{ticket.accountName}
										</a>
									</Row>
								)}
								{ticket.csm && <Row label='CSM'>{getUserReferenceName(ticket.csm)}</Row>}
								{ticket.industry && <Row label='Industry'>{ticket.industry}</Row>}
								{ticket.systemOfRecord && <Row label='System of record'>{ticket.systemOfRecord}</Row>}
								{!!ticket.enabledIntegrations?.length && (
									<Row label='Integrations'>{ticket.enabledIntegrations.join(', ')}</Row>
								)}
							</div>

							<div className={css(s.detailSectionTitle)}>Conversation</div>
							<TicketDetailsEditor
								ticket={ticket}
								categories={categories}
								teamMembers={teamMembers}
								onTicketLeftView={onTicketLeftView}
							/>
							<div className={css(s.detailRows, s.detailRowsStacked)}>
								<Row label='Created'>{formatDate(ticket.creationDate)}</Row>
								<Row label='Messages'>
									{messageCount ?? 0}
									{noteCount ? ` · ${noteCount} notes` : ''}
								</Row>
							</div>

							<PreviousConversations tickets={linkedTickets} onPreviewTicket={setPreviewTicketId} />

							<GitHubPanel
								ticketId={ticketId}
								refetchInterval={false}
								onCreateIssue={() => setGitHubModal('create')}
								onLinkIssue={() => setGitHubModal('link')}
							/>

							{gitHubModal === 'create' && (
								<CreateGitHubIssueModal ticketId={ticketId} ticket={ticket} onClose={() => setGitHubModal(null)} />
							)}
							{gitHubModal === 'link' && (
								<LinkGitHubIssueModal ticketId={ticketId} onClose={() => setGitHubModal(null)} />
							)}
							{previewTicketId && (
								<TicketPreviewModal
									sourceTicketId={previewTicketId}
									targetTicketId={ticketId}
									onClose={() => setPreviewTicketId(null)}
									onMergeSuccess={() => setPreviewTicketId(null)}
									onOpenTicket={id => onSelectTicket?.(id)}
								/>
							)}
						</div>
					)}
				</AppDrawerContent>

				{/* Lazy-mount: AssistantChat fires AI-conversation and account fetches on mount, so
				    keeping it mounted behind the Details tab costs those requests for every ticket
				    selection even when the assistant is never opened. */}
				<AppDrawerContent value={ASSISTANT_DRAWER_VALUE} unmountOnExit styles={[s.supportDrawerContent]}>
					{ticket?.accountId ? (
						<div className={css(s.rightAssistantContent)}>
							<AdminAppAssistantChat
								key={ticket.accountId}
								impersonationContext={{ account: { id: ticket.accountId } }}
							/>
						</div>
					) : (
						<div className={css(s.empty)}>
							<SparklesIcon width={28} height={28} />
							<div>Connect this ticket to an account to use Assistant.</div>
						</div>
					)}
				</AppDrawerContent>

				<AdminDrawerToolbar>
					<AppDrawerToolbarButton value={ASSISTANT_DRAWER_VALUE} tooltip='AI Assistant'>
						<SparklesIcon />
					</AppDrawerToolbarButton>
					<AppDrawerSeparator />
					<AppDrawerToolbarButton value={DETAILS_DRAWER_VALUE} tooltip='Ticket details'>
						<AssigneeIcon />
					</AppDrawerToolbarButton>
				</AdminDrawerToolbar>
			</SideDrawer>
		</AppDrawerProvider>
	);
};
